import React, { useState, useRef, useEffect } from 'react';
import useAiChat from '../hooks/useAiChat';
import { ChatCircleText, X, PaperPlaneRight, ArrowClockwise, CopySimple, Check } from '@phosphor-icons/react';

/**
 * AiChatPanel — floating drawer with the Concierge stream.
 *
 * Props:
 *   surface  : 'private' | 'admin'                 (routes to correct system prompt)
 *   context  : optional string injected server-side (RAG-lite)
 *   storageKey : per-surface session id key (sessionStorage)
 *   theme      : { bg, surface, text, textMuted, border, accent, accentInk } — colour tokens
 *   position   : 'br' (bottom-right, default) | 'bl' (bottom-left)
 *   title      : label at top of drawer
 *   suggestions: initial sample prompts
 */
export default function AiChatPanel({
  surface = 'private',
  context = null,
  storageKey,
  theme,
  position = 'br',
  title = 'TrustFrame Concierge',
  suggestions = [],
  testId = 'ai-chat-panel',
}) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [copied, setCopied] = useState(false);
  const { messages, send, reset, streaming, error } = useAiChat({ surface, context, storageKey });
  const scrollRef = useRef(null);

  const copyTranscript = async () => {
    const md = [
      `# ${title} — Transcript`,
      `_Exported ${new Date().toISOString()}_`,
      '',
      ...messages.map((m) => `**${m.role === 'user' ? 'You' : 'Concierge'}:**\n${m.content}`),
    ].join('\n\n');
    try {
      await navigator.clipboard.writeText(md);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = md;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const T = theme || {
    bg: '#0a0a0b',
    surface: '#111116',
    text: '#f4f4f8',
    textMuted: '#b8b8c8',
    border: '#2a2a30',
    accent: '#f5a623',
    accentInk: '#0a0a0a',
  };

  const submit = (e) => {
    e?.preventDefault();
    const t = input.trim();
    if (!t || streaming) return;
    setInput('');
    send(t);
  };

  const posClass = position === 'bl' ? 'left-4 sm:left-6' : 'right-4 sm:right-6';

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          type="button"
          data-testid={`${testId}-launcher`}
          onClick={() => setOpen(true)}
          className={`fixed bottom-4 sm:bottom-6 ${posClass} z-[70] inline-flex items-center gap-2 px-4 py-3 font-mono uppercase transition-all hover:opacity-95 shadow-lg`}
          style={{
            background: T.accent,
            color: T.accentInk,
            fontSize: 10,
            letterSpacing: '0.25em',
            border: `1px solid ${T.accent}`,
            borderRadius: 999,
            boxShadow: `0 10px 30px ${T.accent}55`,
          }}
        >
          <ChatCircleText size={16} weight="fill" />
          <span className="hidden sm:inline">Ask Concierge</span>
        </button>
      )}

      {/* Drawer */}
      {open && (
        <div
          data-testid={testId}
          className={`fixed bottom-4 sm:bottom-6 ${posClass} z-[70] flex flex-col`}
          style={{
            width: 'min(400px, calc(100vw - 32px))',
            height: 'min(560px, calc(100vh - 100px))',
            background: T.bg,
            color: T.text,
            border: `1px solid ${T.border}`,
            borderRadius: 8,
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${T.border}` }}>
            <div>
              <div className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: '0.28em', color: T.accent }}>
                {title}
              </div>
              <div className="font-mono uppercase mt-1" style={{ fontSize: 8, letterSpacing: '0.2em', color: T.textMuted }}>
                Powered by Claude · TrustFrame AI
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <button
                  type="button"
                  onClick={copyTranscript}
                  data-testid={`${testId}-copy`}
                  aria-label="Copy transcript as Markdown"
                  title="Copy transcript"
                  className="min-h-[32px] min-w-[32px] px-2 inline-flex items-center justify-center opacity-70 hover:opacity-100"
                  style={{ color: copied ? T.accent : T.text }}
                >
                  {copied ? <Check size={14} weight="bold" /> : <CopySimple size={14} weight="bold" />}
                </button>
              )}
              {messages.length > 0 && (
                <button
                  type="button"
                  onClick={reset}
                  data-testid={`${testId}-reset`}
                  aria-label="Start a new conversation"
                  className="min-h-[32px] min-w-[32px] px-2 inline-flex items-center justify-center opacity-70 hover:opacity-100"
                  style={{ color: T.text }}
                >
                  <ArrowClockwise size={14} weight="bold" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid={`${testId}-close`}
                aria-label="Close chat"
                className="min-h-[32px] min-w-[32px] px-2 inline-flex items-center justify-center opacity-70 hover:opacity-100"
                style={{ color: T.text }}
              >
                <X size={14} weight="bold" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
            style={{ background: T.bg }}
          >
            {messages.length === 0 && (
              <div className="space-y-4">
                <p className="font-sans" style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.7 }}>
                  Ask me anything grounded in TrustFrame's documentation — manifesto, whitepaper, ecosystem, calculator, market intel, funding strategy, or legal briefings.
                </p>
                {suggestions.length > 0 && (
                  <div className="space-y-1.5">
                    <div className="font-mono uppercase" style={{ fontSize: 8, letterSpacing: '0.22em', color: T.textMuted }}>
                      Try
                    </div>
                    {suggestions.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => send(s)}
                        data-testid={`${testId}-suggestion`}
                        className="w-full text-left px-3 py-2 font-sans transition-colors"
                        style={{
                          fontSize: 12,
                          color: T.text,
                          border: `1px solid ${T.border}`,
                          background: T.surface,
                          lineHeight: 1.55,
                        }}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className="flex flex-col gap-1">
                <div
                  className="font-mono uppercase"
                  style={{
                    fontSize: 8,
                    letterSpacing: '0.22em',
                    color: m.role === 'user' ? T.accent : T.textMuted,
                  }}
                >
                  {m.role === 'user' ? 'You' : 'Concierge'}
                </div>
                <div
                  className="font-sans whitespace-pre-wrap"
                  style={{
                    fontSize: 13,
                    color: T.text,
                    lineHeight: 1.65,
                    padding: '10px 12px',
                    background: m.role === 'user' ? `${T.accent}14` : T.surface,
                    border: `1px solid ${m.role === 'user' ? `${T.accent}55` : T.border}`,
                  }}
                >
                  {m.content || (streaming && i === messages.length - 1 ? '…' : '')}
                </div>
              </div>
            ))}
            {error && (
              <div className="font-mono" style={{ fontSize: 11, color: '#ff6b6b', padding: '8px 12px', border: '1px solid #ff6b6b33' }}>
                {error}
              </div>
            )}
          </div>

          {/* Composer */}
          <form onSubmit={submit} className="px-3 py-3 flex items-center gap-2" style={{ borderTop: `1px solid ${T.border}` }}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={streaming}
              placeholder={streaming ? 'Thinking…' : 'Ask a question'}
              data-testid={`${testId}-input`}
              className="flex-1 px-3 py-2 bg-transparent outline-none font-sans"
              style={{ fontSize: 13, color: T.text, border: `1px solid ${T.border}` }}
            />
            <button
              type="submit"
              disabled={!input.trim() || streaming}
              data-testid={`${testId}-send`}
              className="min-h-[36px] min-w-[36px] inline-flex items-center justify-center transition-opacity disabled:opacity-40"
              style={{ background: T.accent, color: T.accentInk }}
              aria-label="Send"
            >
              <PaperPlaneRight size={14} weight="bold" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
